import { useCallback, useEffect, useRef, useState } from "react";

export type LiveStatus = "connecting" | "open" | "closed";

export interface LiveMessage {
  t: string;
  [key: string]: unknown;
}

const RETRY_MS = [500, 1000, 2000, 4000, 8000];

function socketUrl(path: string): string {
  const scheme = location.protocol === "https:" ? "wss" : "ws";
  return `${scheme}://${location.host}${path}`;
}

/**
 * A live socket that reconnects on its own.
 *
 * The server answers every connect with a full `state` snapshot rather
 * than a delta, so a reconnect needs no catch-up logic here — refresh,
 * dropped wifi and late join all land in the same place.
 */
export function useLiveSocket(
  path: string | null,
  onMessage: (message: LiveMessage) => void,
  onRefused?: (code: number) => void,
) {
  const [status, setStatus] = useState<LiveStatus>("connecting");
  const socket = useRef<WebSocket | null>(null);
  const attempt = useRef(0);
  const retryTimer = useRef<number | undefined>(undefined);
  const closedByUs = useRef(false);

  // Kept in refs so a new handler identity doesn't tear down the socket.
  const handler = useRef(onMessage);
  handler.current = onMessage;
  const refused = useRef(onRefused);
  refused.current = onRefused;

  useEffect(() => {
    if (!path) return;
    closedByUs.current = false;

    function connect() {
      setStatus("connecting");
      const ws = new WebSocket(socketUrl(path!));
      socket.current = ws;

      ws.onopen = () => {
        attempt.current = 0;
        setStatus("open");
      };
      ws.onmessage = (event) => {
        try {
          handler.current(JSON.parse(event.data) as LiveMessage);
        } catch {
          // A malformed frame is the server's problem, not a reason to drop.
        }
      };
      ws.onclose = (event) => {
        setStatus("closed");
        // 4401/4404/4410 are deliberate refusals — retrying just loops.
        if (closedByUs.current) return;
        if (event.code >= 4400 && event.code < 4500) {
          refused.current?.(event.code);
          return;
        }
        const delay = RETRY_MS[Math.min(attempt.current, RETRY_MS.length - 1)];
        attempt.current += 1;
        retryTimer.current = window.setTimeout(connect, delay);
      };
    }

    connect();
    return () => {
      closedByUs.current = true;
      window.clearTimeout(retryTimer.current);
      socket.current?.close();
    };
  }, [path]);

  const send = useCallback((message: Record<string, unknown>) => {
    if (socket.current?.readyState === WebSocket.OPEN) {
      socket.current.send(JSON.stringify(message));
      return true;
    }
    return false;
  }, []);

  return { status, send };
}
